import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Card, Form } from "react-bootstrap";
import Spinner from "../generics/Spinner";

const getStatusStyle = (status) => {
  const styles = {
    GOOD: { backgroundColor: "#198754", color: "#fff" },
    NEEDS_ATTENTION: { backgroundColor: "#ffc107", color: "#000" },
    MAINTENANCE: { backgroundColor: "#fd7e14", color: "#fff" },
    OUT_OF_SERVICE: { backgroundColor: "#ff5454", color: "#fff" },
  };
  return styles[status] || styles.GOOD;
};

const FacilitiesList = ({
  facilities,
  facilityTypes,
  locations,
  loading,
  error,
  onFilter,
}) => {
  const [filters, setFilters] = useState({
    search: "",
    facilityType: "",
    location: "",
    status: "",
  });

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    if (onFilter) {
      onFilter(newFilters);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (onFilter) {
      onFilter(filters);
    }
  };

  const clearFilters = () => {
    const clearedFilters = { search: "", facilityType: "", location: "", status: "" };
    setFilters(clearedFilters);
    if (onFilter) {
      onFilter(clearedFilters);
    }
  };

  if (loading && facilities.length === 0) {
    return <Spinner />;
  }

  return (
    <div className="facilities-list">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">
          <i className="fa fa-building me-2"></i>
          Facilities
        </h4>
        <Link to="/maintenance/facilities/new" className="btn btn-primary btn-sm">
          <i className="fa fa-plus me-1"></i> Add Facility
        </Link>
      </div>

      {/* Filters */}
      <div className="card mb-3">
        <div className="card-header reports-filter">
          <Form onSubmit={handleSearch} className="row align-items-center mb-0">
            <div className="col-12 col-sm-3">
              <label className="col-form-label filter-label" htmlFor="facilitySearch">
                <i className="fa fa-search mr-05 ml-05"></i>
                Search
              </label>
              <input
                type="text"
                id="facilitySearch"
                className="form-control"
                placeholder="Name, description..."
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
              />
            </div>
            <div className="col-12 col-sm-3">
              <label className="col-form-label filter-label ml-05">
                Type
              </label>
              <select
                className="form-control"
                value={filters.facilityType}
                onChange={(e) => handleFilterChange("facilityType", e.target.value)}
              >
                <option value="">All Types</option>
                {facilityTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-2">
              <label className="col-form-label filter-label ml-05">
                Location
              </label>
              <select
                className="form-control"
                value={filters.location}
                onChange={(e) => handleFilterChange("location", e.target.value)}
              >
                <option value="">All Locations</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-2">
              <label className="col-form-label filter-label ml-05">
                Status
              </label>
              <select
                className="form-control"
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <option value="">All Status</option>
                <option value="GOOD">Good</option>
                <option value="NEEDS_ATTENTION">Needs Attention</option>
                <option value="MAINTENANCE">Under Maintenance</option>
                <option value="OUT_OF_SERVICE">Out of Service</option>
              </select>
            </div>
            <div className="col-12 col-sm-2 d-flex align-items-end" style={{ paddingTop: "32px" }}>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={clearFilters}>
                <i className="fa fa-times me-1"></i> Clear
              </button>
            </div>
          </Form>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="alert alert-danger py-2">
          <i className="fa fa-exclamation-circle me-2"></i>
          {error.message || "Error loading facilities"}
        </div>
      )}

      {/* Results */}
      <Card>
        <Card.Body className="p-0">
          {facilities.length === 0 ? (
            <div className="shipment-list--empty text-center py-5">
              <i className="fa fa-building fa-3x mb-3 text-muted"></i>
              <p className="mb-0 text-muted">No facilities found</p>
            </div>
          ) : (
            <table className="table table-striped table-bordered datatable mb-0">
              <thead>
                <tr>
                  <th>Facility</th>
                  <th>Type</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th>Area Size</th>
                  <th>Pending Tasks</th>
                  <th className="text-center" style={{ width: "100px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {facilities.map((facility) => (
                  <tr key={facility.id}>
                    <td>
                      <Link to={`/maintenance/facilities/${facility.id}`}>
                        <strong>{facility.name}</strong>
                      </Link>
                      {facility.description && (
                        <small className="d-block text-muted text-truncate" style={{ maxWidth: "200px" }}>
                          {facility.description}
                        </small>
                      )}
                    </td>
                    <td>{facility.facility_type_name}</td>
                    <td>{facility.location_name}</td>
                    <td>
                      <span className="badge badge-pill" style={getStatusStyle(facility.status)}>
                        {facility.status_display || facility.status}
                      </span>
                    </td>
                    <td>{facility.area_size || "-"}</td>
                    <td>
                      {facility.pending_tasks_count > 0 ? (
                        <span className="badge badge-pill" style={{ backgroundColor: "#ffc107", color: "#000" }}>
                          {facility.pending_tasks_count}
                        </span>
                      ) : (
                        <span className="text-muted">0</span>
                      )}
                    </td>
                    <td className="text-center">
                      <Link
                        to={`/maintenance/facilities/${facility.id}`}
                        className="btn btn-sm btn-outline-primary me-1"
                        title="View"
                      >
                        <i className="fa fa-eye"></i>
                      </Link>
                      <Link
                        to={`/maintenance/facilities/${facility.id}/edit`}
                        className="btn btn-sm btn-outline-secondary"
                        title="Edit"
                      >
                        <i className="fa fa-pencil"></i>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card.Body>
      </Card>

      {facilities.length > 0 && (
        <div className="text-muted small mt-2">
          Showing {facilities.length} facilit{facilities.length !== 1 ? "ies" : "y"}
        </div>
      )}
    </div>
  );
};

export default FacilitiesList;
